// Страницы журнала из history_page: первая — последние действия, Load more — старше последней строки; живой
// хвост — по событиям стора первая страница перечитывается и сливается с уже загруженным (по id).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@core/supabase';
import { addDays, mondayOf, today, type ISODate } from '@core/dates';
import { data, useData } from '@core/store';
import { groupActions, type Action, type HistoryRow } from './planner';

export type HistoryFilter = {
  actor?: string | null; from?: string | null; to?: string | null;   // to — исключительно
  table?: string | null; row?: string | null; field?: string | null; kind?: string | null; q?: string | null;
};

/** Пресеты периода: границы как timestamp, `to` — исключительно (следующая полночь). */
export type Period = 'today' | 'week' | 'month' | 'all';
export const periodRange = (p: Period, now: ISODate = today()): { from: string | null; to: string | null } => {
  if (p === 'all') return { from: null, to: null };
  const from = p === 'today' ? now : p === 'week' ? mondayOf(now) : addDays(now, -30);
  return { from: `${from}T00:00:00`, to: `${addDays(now, 1)}T00:00:00` };
};

const PAGE = 50;
const LIVE_DEBOUNCE_MS = 400;

type Args = Record<string, unknown>;
const argsOf = (f: HistoryFilter, before: number | null): Args => ({
  page: PAGE, before_id: before ?? undefined, p_actor: f.actor ?? undefined, from_at: f.from ?? undefined, to_at: f.to ?? undefined,
  p_table: f.table ?? undefined, p_row: f.row ?? undefined, p_field: f.field ?? undefined, p_kind: f.kind ?? undefined, q: f.q?.trim() || undefined,
});

export function useHistory(filter: HistoryFilter): { actions: Action[]; rows: HistoryRow[]; loadMore: () => void; hasMore: boolean; loading: boolean; error: string | null; reload: () => void } {
  const [rows, setRows] = useState<Map<number, HistoryRow>>(new Map());
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true); // первая страница ещё не пришла — не рисовать «пусто»
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(filter);
  const gen = useRef(0);

  const fetchPage = useCallback(async (before: number | null): Promise<HistoryRow[] | null> => {
    const { data: page, error: err } = await supabase.rpc('history_page', argsOf(JSON.parse(key) as HistoryFilter, before) as never);
    if (err) { setError(err.message); return null; }
    setError(null);
    return (page ?? []) as unknown as HistoryRow[];
  }, [key]);

  // новая выборка при смене фильтра
  useEffect(() => {
    const my = ++gen.current;
    setRows(new Map()); setHasMore(true); setLoading(true);
    void fetchPage(null).then((page) => {
      if (my !== gen.current) return;
      setLoading(false);
      if (!page) return;
      setRows(new Map(page.map((r) => [r.id, r])));
      setHasMore(page.length >= PAGE);
    });
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    const oldest = Math.min(...rows.keys());
    if (!Number.isFinite(oldest)) return;
    const my = gen.current;
    setLoading(true);
    void fetchPage(oldest).then((page) => {
      if (my !== gen.current) return;
      setLoading(false);
      if (!page) return;
      setRows((prev) => { const next = new Map(prev); for (const r of page) next.set(r.id, r); return next; });
      setHasMore(page.length >= PAGE);
    });
  }, [loading, hasMore, rows, fetchPage]);

  // живой хвост: применили события — перечитать первую страницу и слить; чужие и свои записи одинаково
  const applied = useData((s) => s.appliedEvents);
  const reload = useCallback(() => {
    const my = gen.current;
    void fetchPage(null).then((page) => {
      if (my !== gen.current || !page) return;
      setRows((prev) => { const next = new Map(prev); for (const r of page) next.set(r.id, r); return next; });
    });
  }, [fetchPage]);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const t = setTimeout(reload, LIVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [applied, reload]);

  const sorted = useMemo(() => [...rows.values()].sort((a, b) => b.id - a.id), [rows]);
  const actions = useMemo(() => groupActions(sorted), [sorted]);
  return { actions, rows: sorted, loadMore, hasMore, loading, error, reload };
}

const asHistoryRow = (r: unknown): HistoryRow => ({ ...(r as Omit<HistoryRow, 'row_label' | 'tx'>), tx: String((r as { tx: unknown }).tx), row_label: null });

/** Все строки транзакции — для восстановления с каскадом: страница под фильтром могла их отсеять. */
export async function txRows(tx: string): Promise<HistoryRow[]> {
  const { data: rows, error } = await supabase.from('change_log').select('*').eq('tx', tx as never).order('id', { ascending: false });
  if (error) throw new Error(error.message);
  return (rows ?? []).map(asHistoryRow);
}

/** Правки по тем же полям после строки журнала — предупреждение перед откатом. */
export async function laterChanges(row: HistoryRow): Promise<HistoryRow[]> {
  const { data: rows, error } = await supabase.rpc('later_changes', { p_table: row.table_name, p_row: row.row_id, fields: row.changed, after_id: row.id });
  if (error) throw new Error(error.message);
  return (rows ?? []).map(asHistoryRow);
}

/** Живая строка для отката: подтверждённое состояние стора (не замыкание рендера). */
export const liveRow = (table: string, id: string): { updated_at: string } | null => {
  const t = data.store.getState().tables as Record<string, Map<string, { updated_at: string }>>;
  return t[table]?.get(id) ?? null;
};
